/**
 * Login Page
 *
 * Email/password login with validation and error handling.
 * Includes Google OAuth support when configured.
 */

import { useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { FiEye, FiEyeOff, FiAlertCircle } from 'react-icons/fi'
import { FcGoogle } from 'react-icons/fc'
import { FaApple } from 'react-icons/fa'
import { useAuthStore, useDemoModeStore } from '../../store'
import { getContextHomeRoute } from '../../lib/contextHelpers'
import { apiConfig } from '../../api'
import { useGoogleLogin } from '../../providers'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Alert, AlertTitle } from '../../components/ui/alert'

/**
 * Google Login Button - only rendered when Google OAuth is configured
 * This component must be mounted inside GoogleOAuthProvider
 */
function GoogleLoginButton({
  onSuccess,
  onError,
}: {
  onSuccess: (token: string) => void
  onError: () => void
}) {
  const [isLoading, setIsLoading] = useState(false)

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setIsLoading(true)
      onSuccess(tokenResponse.access_token)
    },
    onError: () => {
      setIsLoading(false)
      onError()
    },
  })

  return (
    <Button
      variant="outline"
      className="w-full"
      onClick={() => handleGoogleLogin()}
      disabled={isLoading}
    >
      <div className="flex items-center gap-2">
        <FcGoogle size={20} />
        <span>{isLoading ? 'Signing in with Google...' : 'Continue with Google'}</span>
      </div>
    </Button>
  )
}

/**
 * Apple Login Button - only rendered when Apple Sign-In is configured
 */
function AppleLoginButton({
  onSuccess,
  onError,
}: {
  onSuccess: (data: { idToken: string; authorizationCode: string; user?: any }) => void
  onError: () => void
}) {
  const [isLoading, setIsLoading] = useState(false)

  const handleAppleLogin = async () => {
    try {
      setIsLoading(true)
      const response = await (window as any).AppleID.auth.signIn()
      onSuccess({
        idToken: response.authorization.id_token,
        authorizationCode: response.authorization.code,
        user: response.user,
      })
    } catch {
      setIsLoading(false)
      onError()
    }
  }

  return (
    <Button
      variant="outline"
      className="w-full"
      onClick={handleAppleLogin}
      disabled={isLoading}
    >
      <div className="flex items-center gap-2">
        <FaApple size={20} />
        <span>{isLoading ? 'Signing in with Apple...' : 'Continue with Apple'}</span>
      </div>
    </Button>
  )
}

export function Login() {
  const navigate = useNavigate()
  const { login, googleLogin, appleLogin, isLoading, error, clearError } = useAuthStore()

  const clearStaleDemoMode = () => {
    const { isActive } = useDemoModeStore.getState()
    if (isActive) {
      useDemoModeStore.setState({
        isActive: false,
        activePersonaKey: null,
        savedSession: null,
        savedAuthStore: null,
        isTransitioning: false,
      })
    }
  }

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [touched, setTouched] = useState({ email: false, password: false })

  // Validation
  const emailError = touched.email && !email ? 'Email is required' : ''
  const passwordError = touched.password && !password ? 'Password is required' : ''

  // Check if OAuth providers are configured
  const isGoogleEnabled = !!apiConfig.googleClientId && !apiConfig.useMockApi
  const isAppleEnabled = !!apiConfig.appleClientId && !apiConfig.useMockApi

  const handleGoogleSuccess = async (token: string) => {
    clearError()
    const success = await googleLogin(token)
    if (success) {
      clearStaleDemoMode()
      const { activeContext, user: loggedInUser } = useAuthStore.getState()
      navigate(getContextHomeRoute(activeContext, loggedInUser ?? undefined), { replace: true })
    }
  }

  const handleGoogleError = () => {
    clearError()
  }

  const handleAppleSuccess = async (data: { idToken: string; authorizationCode: string; user?: any }) => {
    clearError()
    const success = await appleLogin(data)
    if (success) {
      clearStaleDemoMode()
      const { activeContext, user: loggedInUser } = useAuthStore.getState()
      navigate(getContextHomeRoute(activeContext, loggedInUser ?? undefined), { replace: true })
    }
  }

  const handleAppleError = () => {
    clearError()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()

    // Mark all fields as touched
    setTouched({ email: true, password: true })

    // Validate
    if (!email || !password) return

    const success = await login({ email, password })

    if (success) {
      clearStaleDemoMode()
      const { activeContext, user: loggedInUser } = useAuthStore.getState()
      navigate(getContextHomeRoute(activeContext, loggedInUser ?? undefined), { replace: true })
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-secondary px-4">
      <div className="w-full max-w-md p-8 bg-background rounded-[var(--radius-card)] border border-border shadow-sm">
        <div className="flex flex-col gap-6">
          {/* Header */}
          <div className="flex flex-col gap-2 text-center">
            <h1 className="text-2xl font-bold text-foreground">
              Welcome back
            </h1>
            <p className="text-foreground-muted">Sign in to continue to DoCurious</p>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <FiAlertCircle className="h-4 w-4" />
              <AlertTitle>{error}</AlertTitle>
            </Alert>
          )}

          {/* OAuth Buttons */}
          <div className="flex flex-col gap-3">
            {isGoogleEnabled ? (
              <GoogleLoginButton
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
              />
            ) : (
              <Button
                variant="outline"
                className="w-full"
                disabled
                title="Google Sign-In is not yet configured"
              >
                <div className="flex items-center gap-2 opacity-50">
                  <FcGoogle size={20} />
                  <span>Continue with Google</span>
                </div>
              </Button>
            )}
            {isAppleEnabled ? (
              <AppleLoginButton
                onSuccess={handleAppleSuccess}
                onError={handleAppleError}
              />
            ) : (
              <Button
                variant="outline"
                className="w-full"
                disabled
                title="Apple Sign-In is not yet configured"
              >
                <div className="flex items-center gap-2 opacity-50">
                  <FaApple size={20} />
                  <span>Continue with Apple</span>
                </div>
              </Button>
            )}
          </div>

          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-border" />
            <span className="text-foreground-muted text-sm">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-4">
              {/* Email Field */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                  placeholder="you@example.com"
                />
                {emailError && <p className="text-sm text-error">{emailError}</p>}
              </div>

              {/* Password Field */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Password</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                    placeholder="Enter your password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-foreground-muted hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                  </button>
                </div>
                {passwordError && <p className="text-sm text-error">{passwordError}</p>}
              </div>

              {/* Forgot Password Link */}
              <div className="w-full text-right">
                <RouterLink to="/forgot-password" className="text-sm text-primary hover:underline">
                  Forgot password?
                </RouterLink>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </Button>
            </div>
          </form>

          {/* Register Link */}
          <p className="text-center text-foreground-muted">
            Don't have an account?{' '}
            <RouterLink to="/register" className="text-primary hover:underline">
              Create one
            </RouterLink>
          </p>

          {/* Demo Credentials - only show in mock mode */}
          {apiConfig.useMockApi && (
            <div className="p-4 bg-background-secondary rounded-[var(--radius-button)] text-sm">
              <p className="font-semibold text-foreground mb-2">
                Demo Credentials
              </p>
              <p className="text-foreground-muted">
                Email: demo@docurious.com
                <br />
                Password: DcDemo_2026!
              </p>
            </div>
          )}

          {/* Demo Mode entry */}
          {apiConfig.useMockApi && (
            <div className="text-center">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/demo')}
              >
                Try Interactive Demo
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
